import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import {useNavigate, useParams} from "react-router-dom";
import { useTranslation } from "react-i18next";
import view from './DocumentPage.view';
import logo from "../../../../assets/logo/logo 3.png";
import {FILE_TYPE_MODEL, FILE_TYPE_SIDE_MODEL} from "../../../Dashboard/Visitor/Visitor/Model/Model";
import {
    ValidateObverseDocument,
    ValidateReverseDocument,
    VeridasServiceProcess
} from "../../../../reduxToolkit/Visitor/VisitorApi";
import { setIsSuccessValidation, setUserDataDocument } from "../../../../reduxToolkit/Visitor/VisitorSlice";
import { toast } from "react-toastify";
import { loadScript } from "../../Model/Models";
import FullPageLoader from '../../../../utils/FullPageLoader';
import routes from '../../../../Routes/routes';
import {colors} from '../../../../enums/colors';
import useHandleBeforeUnload from '../../Functionality/useHandleBeforeUnload';
import useBackNavigation from '../../Functionality/useBackNavigation';
import {getUrlParameters} from "../../../Dashboard/Visitor/External/Model/Model";

/**
 *
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 */
const DocumentPage = () => {
    const { id, guardFlowId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const targetRef = useRef(null);

    const primaryColor = colors.primary;
    const general00Opacity66 = colors.general00Opacity66;
    const general950Opacity10 = colors.general950Opacity10;

    const selectedDocumentType = useSelector((state) => state.VisitorSlice.selectedDocumentType);
    const idProcess = useSelector((state) => state.VisitorSlice.getValidationId);
    const { getInvitationById } = useSelector((state) => state.InvitationSlice);
    const userRestrictionData = useSelector((state) => state.VisitorSlice.getUserRestrictionVisitor);

    const [unmounted, setUnmounted] = useState(false);
    const [obverseImage, setObverseImage] = useState(null);
    const [reverseImage, setReverseImage] = useState(null);
    const vdDocument = useRef(null);
    const [loading, setLoading] = useState(false);

    const onRuntimeInitialized = () => {
        if (window.cv) {
            if (window.cv instanceof Promise) {
                window.cv.then((cv) => {
                    window.cv = cv;
                    sdkStart();
                });
            } else {
                sdkStart();
            }
        }
    };

    const sdkStart = () => {
        vdDocument.current = window.makeVDDocumentWidget();
        let params = {
            targetSelector: '#target',
            sdkBackgroundColorInactive: general00Opacity66,
            outerGlowCenteringAidDefault: general950Opacity10,
            outerGlowCenteringAidDetecting: primaryColor,
            customLogo: logo
        };
        if (selectedDocumentType === FILE_TYPE_MODEL.INE.identifier) {
            params.documents = [FILE_TYPE_MODEL.INE.identifier];
        } else {
            params.passport = [FILE_TYPE_MODEL.PASSPORT.identifier];
        }

        vdDocument.current(params);
    };

    const onVDDocumentMounted = () => {
        setUnmounted(false);
    };

    const onVDDocumentMountFailure = (e) => {};
    const onVDDocumentUnmounted = () => {
        setUnmounted(true);
    };
    const onVDDocumentObverseDetection = (e) => setObverseImage(e.detail);
    const onVDDocumentReverseDetection = (e) => setReverseImage(e.detail);


    useEffect(() => {
        if (!selectedDocumentType) return;
        const target = document.getElementById('target');

        target.addEventListener('VDDocument_mounted', onVDDocumentMounted);
        target.addEventListener('VDDocument_mountFailure', onVDDocumentMountFailure);
        target.addEventListener('VDDocument_unmounted', onVDDocumentUnmounted);
        target.addEventListener('VDDocument_obverseDetection', onVDDocumentObverseDetection);
        target.addEventListener('VDDocument_reverseDetection', onVDDocumentReverseDetection);

        // Load scripts
        loadScript('/js/opencv/cv_alive.js', () => {
            loadScript('/js/VDDocument.js', onRuntimeInitialized);
        });

        return () => {
            if (!unmounted) window.destroyVDDocumentWidget();
        };
    }, [selectedDocumentType]);

    const handleDestroy = async () => {
        if (typeof window.destroyVDDocumentWidget === 'function') {
            await window.destroyVDDocumentWidget();
        }
    }

    const handleFailed = async (invitationId) => {
        setLoading(false);
        navigate(getUrlParameters({ url: routes.veridasIntegration.validationFail, id: invitationId, guardFlowId }));
        toast.error(t('error_validating_the_information_please_try_again'));
    }

    const handleSaveIne = async () => {
        setLoading(true);
        const invitationId = getInvitationById?.invitation?.id;
        const userId = getInvitationById?.invitation?.visitorId;
        const obverse = {
            analysisType: FILE_TYPE_SIDE_MODEL.OBVERSE.identifier,
            documentImage: obverseImage,
            documentType: selectedDocumentType,
            validationId: idProcess,
            userId: userId,
        };
        await dispatch(ValidateObverseDocument(obverse)).then(({ payload: { data: { data } } }) => {
            if (data) {
                const reverse = {
                    analysisType: FILE_TYPE_SIDE_MODEL.REVERSE.identifier,
                    documentImage: reverseImage,
                    documentType: selectedDocumentType,
                    validationId: idProcess,
                    userId: userId,
                };
                dispatch(ValidateReverseDocument(reverse)).then(({ payload: { data: { data } } }) => {
                    if (data) {
                        documentValidationStatus(idProcess);
                    }else{
                        handleFailed(invitationId);
                    }
                });
            }else{
                handleFailed(invitationId);
            }
        });
    };

    const handleSavePassport = () => {
        setLoading(true);
        const invitationId = getInvitationById?.invitation?.id;
        const userId = getInvitationById?.invitation?.visitorId;
        const obverse = {
            analysisType: FILE_TYPE_SIDE_MODEL.OBVERSE.identifier,
            documentImage: obverseImage,
            documentType: selectedDocumentType,
            validationId: idProcess,
            userId: userId,
        };
        dispatch(ValidateObverseDocument(obverse)).then(({ payload: { data: { data } } }) => {
            if (data) {
                documentValidationStatus(idProcess);
            } else {
                handleFailed(invitationId);
            }
        });
    };

    const documentValidationStatus = async (validationId) => {
        const invitationId = getInvitationById?.invitation?.id;
        const userId = getInvitationById?.invitation?.visitorId;
        const { payload: { data: { data, success } } } = await dispatch(VeridasServiceProcess({ validationId , userId }));
        if (success) {
            setLoading(false);
            dispatch(setUserDataDocument(data));
            dispatch(setIsSuccessValidation(success));

            if(userRestrictionData?.biometricVisitor == true) {
                navigate(getUrlParameters({ url: routes.veridasIntegration.selfieValidation + id + "/", id:validationId, guardFlowId}));
            }else{
                navigate(getUrlParameters({ url: routes.veridasIntegration.successValidation, id:invitationId, guardFlowId }));
            }
        } else {
            await handleFailed(invitationId);
        }
    };

    const destroyCameraAndSave = async () => {
        await handleDestroy();
        if (selectedDocumentType === FILE_TYPE_MODEL.INE.identifier) {
            await handleSaveIne();
        } else if (selectedDocumentType === FILE_TYPE_MODEL.PASSPORT.identifier) {
            await handleSavePassport();
        }
    }

    useEffect(() => {
        if (
            (selectedDocumentType === FILE_TYPE_MODEL.INE.identifier && obverseImage && reverseImage) ||
            (selectedDocumentType === FILE_TYPE_MODEL.PASSPORT.identifier && obverseImage)
        ) {
            destroyCameraAndSave();
        }
    }, [obverseImage, reverseImage]);

    useBackNavigation({getInvitationById, validationId: idProcess });
    useHandleBeforeUnload({ getInvitationById ,validationId: idProcess });
    return loading ? <FullPageLoader /> :
        view({
            obverseImage,
            reverseImage,
            targetRef
        });

};

export default DocumentPage;
